import React, { useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Check, Sparkles, Zap, Infinity as InfIcon, Heart, Bookmark } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const plans = [
    {
        id: "monthly",
        label: "Monthly",
        price: "$4.99",
        period: "/mo",
        note: "Billed monthly. Cancel anytime.",
    },
    {
        id: "yearly",
        label: "Yearly",
        price: "$39",
        period: "/yr",
        note: "≈ $3.25/mo — save 35%",
        best: true,
    },
];

const FEATURES = [
    { icon: InfIcon, text: "Unlimited fact-checks" },
    { icon: Zap, text: "Priority processing speed" },
    { icon: Bookmark, text: "Save & organize checks in your library" },
    { icon: Check, text: "Ad-free experience" },
    { icon: Sparkles, text: "Branded shareable fact cards" },
    { icon: Heart, text: "50% of your subscription funds Hands of Hope" },
];

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selected, setSelected] = useState("yearly");
    const [loading, setLoading] = useState(false);
    const [params] = useSearchParams();
    const limitReached = params.get("reason") === "limit";

    const upgrade = async () => {
        if (!user) { navigate("/signup", { state: { from: "/pricing" } }); return; }
        if (user.is_pro) { toast.info("You're already Pro"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/subscription/checkout", {
                plan: selected,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout");
            setLoading(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
            <div className="max-w-2xl">
                <p className="text-xs uppercase tracking-widest text-primary mb-3">Membership</p>
                <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight">
                    Fact-check the world.<br />
                    <span className="text-primary">Fund something bigger.</span>
                </h1>
                <p className="text-muted-foreground text-lg mt-4 leading-relaxed">
                    Half of every Pro subscription funds Hands of Hope. The other half keeps the fact-checker
                    running independently, ad-free, and free from partisan influence.
                </p>
                {limitReached && (
                    <div className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200" data-testid="paywall-limit-banner">
                        You&apos;ve hit your free daily limit. Upgrade for unlimited checks.
                    </div>
                )}
            </div>

            <div className="mt-12 grid lg:grid-cols-3 gap-6">
                <Card className="card-surface p-8 rounded-2xl">
                    <h3 className="font-display text-2xl text-accent">Free</h3>
                    <p className="text-muted-foreground text-sm mt-1">For casual verifiers</p>
                    <div className="mt-6">
                        <div className="font-display text-5xl text-accent">$0</div>
                        <div className="text-xs text-muted-foreground mt-1">forever</div>
                    </div>
                    <ul className="mt-8 space-y-3 text-sm">
                        {[
                            "3 fact-checks per day",
                            "Traffic-light verdicts",
                            "Source citations",
                            "Trending feed",
                        ].map((f) => (
                            <li key={f} className="flex items-start gap-2 text-muted-foreground">
                                <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                            </li>
                        ))}
                    </ul>
                </Card>

                <Card className="card-surface p-8 rounded-2xl lg:col-span-2 relative overflow-hidden border-primary/40 badge-glow-primary">
                    <div className="absolute inset-0 pointer-events-none opacity-20"
                         style={{ background: "radial-gradient(circle at 100% 0%, rgba(61,44,224,0.6), transparent 60%)" }} />
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-display text-2xl text-accent">Pro</h3>
                            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                                7-day free trial
                            </span>
                        </div>
                        <p className="text-muted-foreground text-sm mt-1">For pros, journalists, and truth-diggers</p>

                        <div className="mt-6 grid sm:grid-cols-2 gap-3">
                            {plans.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setSelected(p.id)}
                                    data-testid={`plan-${p.id}-button`}
                                    className={`text-left rounded-xl border p-5 transition-colors ${
                                        selected === p.id
                                            ? "border-primary bg-primary/15"
                                            : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">
                                            {p.label}
                                        </span>
                                        {p.best && (
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
                                                Best value
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-baseline gap-1">
                                        <span className="font-display text-4xl text-accent">{p.price}</span>
                                        <span className="text-muted-foreground">{p.period}</span>
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-2">{p.note}</p>
                                </button>
                            ))}
                        </div>

                        <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                            {FEATURES.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-accent/90">
                                    <f.icon className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f.text}
                                </li>
                            ))}
                        </ul>

                        <Button
                            onClick={upgrade}
                            disabled={loading || user?.is_pro}
                            data-testid="pricing-upgrade-button"
                            className="w-full h-12 mt-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base shadow-lg shadow-primary/30"
                        >
                            {user?.is_pro
                                ? "You're already Pro"
                                : loading
                                    ? "Redirecting…"
                                    : (<>Start 7-day free trial · {selected === "yearly" ? "$39/yr" : "$4.99/mo"}</>)}
                        </Button>
                        <p className="text-[11px] text-muted-foreground mt-3 text-center">
                            Cancel anytime. Powered by Stripe.
                        </p>
                    </div>
                </Card>
            </div>
        </div>
    );
}
